import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from "@trpc/client";

import { useCallback, useMemo, useState } from "react";

import type { AppRouter, ProcedurePath } from "@goose/api";

import { useAcquireToken } from "~/hooks/useAcquireToken";
import { TRPCProvider } from "~/lib/trpc";

// Procedures the ESPv2 endpoint routes to goose-api-long-running via literal
// paths in terragrunt/apps/endpoint/config/goose-openapi.yaml. They must never
// be batched: a batched URL joins procedure names with commas, misses the
// literal path, and falls through to goose-api.
const LONG_RUNNING_PROCEDURES: ReadonlySet<string> = new Set([
  "contacts.evaluations.ensure",
  "opportunities.requirements.extract",
] satisfies ProcedurePath[]);

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const acquireToken = useAcquireToken();

  const getHeaders = useCallback(async () => {
    const token = await acquireToken();
    return { Authorization: `Bearer ${token.accessToken}` };
  }, [acquireToken]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
          },
        },
      }),
  );

  const trpcClient = useMemo(
    () =>
      createTRPCClient<AppRouter>({
        links: [
          splitLink({
            condition(op) {
              return (
                op.context["skipBatch"] === true ||
                LONG_RUNNING_PROCEDURES.has(op.path)
              );
            },
            true: httpLink({
              url: "/api/trpc",
              headers: getHeaders,
            }),
            false: httpBatchLink({
              url: "/api/trpc",
              headers: getHeaders,
            }),
          }),
        ],
      }),
    [getHeaders],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
